import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWorkspaceSelection } from "./WorkspaceSelectionContext";
import BulkSipStatusActions from "../bulkSipActions/BulkSipStatusActions";
import BulkSipTableEditorAction from "../bulkSipActions/BulkSipTableEditorAction";
import SessionChangedBadge from "../sessionActivity/SessionChangedBadge";
import BulkComplexTableEditorAction from "../bulkComplexActions/BulkComplexTableEditorAction";
import { getWorkspaceSelectionKey } from "./workspaceSelectionUtils";
import AdminStatusBadge from "../shared/AdminStatusBadge";
import ActionIconButton from "../shared/ActionIconButton";
import { stopCurrentSipClient } from "../../../admin/api";
import { useAdminNotifications } from "../notifications/AdminNotificationsContext";
import { useAdminDataRefresh } from "../dataRefresh/AdminDataRefreshContext";
import { useSessionSipActivity } from "../sessionActivity/SessionSipActivityContext";
import "./WorkspaceSelectionDrawer.css";
import { useWorkspaceSelectionMode } from "./WorkspaceSelectionModeContext";

const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "a[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function prefersReducedMotion() {
  return typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function MetaRow({ label, children }) {
  return (
    <div className="workspace-selection-card__meta-row">
      <span className="workspace-selection-card__meta-label">{label}</span>
      <span className="workspace-selection-card__meta-value">{children || "—"}</span>
    </div>
  );
}

function RemoveButton({ label, onClick }) {
  return (
    <ActionIconButton
      icon="trash"
      label={label}
      tone="danger"
      onClick={onClick}
    />
  );
}

function SelectionGroup({ title, count, openLabel, onOpen, actions, children }) {
  return (
    <section className="workspace-selection-group">
      <div className="workspace-selection-group__header">
        <h3 className="workspace-selection-group__title">{title}</h3>
        <div className="workspace-selection-group__header-actions">
          <span className="workspace-selection-count" aria-label={`${count} элементов`}>
            {count}
          </span>
          <button type="button" className="workspace-selection-action" onClick={onOpen}>{openLabel}</button>
        </div>
      </div>
      <div className="workspace-selection-group__actions">{actions}</div>
      <div className="workspace-selection-group__list">{children}</div>
    </section>
  );
}

export default function WorkspaceSelectionDrawer({ isOpen, onClose, returnFocusRef }) {
  const { items, removeItem, clearItems, updateComplexReferences } = useWorkspaceSelection();
  const { notify } = useAdminNotifications();
  const { invalidate } = useAdminDataRefresh();
  const { recordChanges } = useSessionSipActivity();
  const { enterMode } = useWorkspaceSelectionMode();
  const [stoppingComplexIds, setStoppingComplexIds] = useState(() => new Set());
  const [isMounted, setIsMounted] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);
  const drawerRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);
  const wasOpenRef = useRef(false);
  const visibleRef = useRef(false);
  const groups = useMemo(
    () => ({
      complexes: items.filter((item) => item.entityType === "complex"),
      sipClients: items.filter((item) => item.entityType === "sipClient"),
      transfers: items.filter((item) => item.type === "transfer"),
    }),
    [items]
  );

  const finishClosing = useCallback(() => {
    visibleRef.current = false;
    setIsVisible(false);
    setIsMounted(false);
    const focusTarget = returnFocusRef?.current || previousFocusRef.current;
    if (focusTarget && typeof focusTarget.focus === "function") {
      focusTarget.focus();
    }
  }, [returnFocusRef]);

  const requestClose = useCallback(() => {
    if (drawerRef.current?.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      if (!wasOpenRef.current) {
        previousFocusRef.current = document.activeElement;
      }
      wasOpenRef.current = true;
      setIsMounted(true);
      return;
    }

    wasOpenRef.current = false;
    const hadEntered = visibleRef.current;
    visibleRef.current = false;
    setIsVisible(false);
    if (isMounted && (!hadEntered || prefersReducedMotion())) {
      finishClosing();
    }
  }, [finishClosing, isMounted, isOpen]);

  useEffect(() => {
    if (!isMounted || !isOpen) return undefined;

    const frame = window.requestAnimationFrame(() => {
      visibleRef.current = true;
      setIsVisible(true);
      closeButtonRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isMounted, isOpen]);

  useEffect(() => {
    if (!isMounted) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMounted]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !event.defaultPrevented) {
        event.preventDefault();
        requestClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, requestClose]);

  if (!isMounted) return null;

  const isActive = isOpen && isVisible;
  const handleDialogKeyDown = (event) => {
    if (event.key !== "Tab" || !drawerRef.current) return;

    const focusable = Array.from(
      drawerRef.current.querySelectorAll(FOCUSABLE_SELECTOR)
    ).filter((element) => element.getAttribute("aria-hidden") !== "true");
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleDrawerTransitionEnd = (event) => {
    if (
      event.target === event.currentTarget &&
      event.propertyName === "transform" &&
      !isOpen
    ) {
      finishClosing();
    }
  };

  const actionClassName = "workspace-selection-action";
  const openGroup = (group) => {
    enterMode(group);
    requestClose();
  };
  const handleStopComplex = async (item) => {
    const complexId = String(item?.complexId || "");
    if (!complexId || item.snapshot?.rootStatus === false || stoppingComplexIds.has(complexId)) return;

    setStoppingComplexIds((current) => new Set(current).add(complexId));
    try {
      const stopped = await stopCurrentSipClient(complexId);
      updateComplexReferences([{ complexId, complex: stopped }]);
      if (item.snapshot?.currentSipClientId) {
        recordChanges([{
          complexId,
          sipClientId: item.snapshot.currentSipClientId,
          clientName: item.snapshot.currentSipClient,
          changedFields: ["complexStatus", "currentSipClientId"],
          snapshot: {
            complexName: stopped?.name || item.snapshot?.complexName || "Без названия",
            complexStatus: false,
            currentSipClient: "",
            currentSipClientId: "",
          },
        }], "workspace-selection");
      }
      invalidate(["complexes", "sip", "transfers"]);
      notify("ЖК остановлен на карте", "success");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось остановить ЖК на карте.", "error");
    } finally {
      setStoppingComplexIds((current) => {
        const next = new Set(current);
        next.delete(complexId);
        return next;
      });
    }
  };

  return (
    <div
      className={`workspace-selection-overlay${isActive ? " workspace-selection-overlay--open" : ""}`}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && isOpen) requestClose();
      }}
    >
      <aside
        id="workspace-selection-drawer"
        ref={drawerRef}
        className={`workspace-selection-drawer${isActive ? " workspace-selection-drawer--open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="workspace-selection-title"
        aria-hidden={!isOpen}
        inert={isOpen ? undefined : ""}
        onKeyDown={handleDialogKeyDown}
        onTransitionEnd={handleDrawerTransitionEnd}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="workspace-selection-header">
          <div className="workspace-selection-header__text">
            <div className="workspace-selection-header__title-row">
              <h2 id="workspace-selection-title" className="workspace-selection-title">
                Рабочая подборка
              </h2>
              <span className="workspace-selection-count workspace-selection-count--total">
                {items.length}
              </span>
            </div>
            <p className="workspace-selection-header__subtitle">
              Сохранённые элементы для групповой работы
            </p>
          </div>
          <div className="workspace-selection-header__actions">
            {items.length > 0 && (
              <button
                type="button"
                className="workspace-selection-header-button workspace-selection-header-button--danger"
                onClick={clearItems}
              >
                Очистить
              </button>
            )}
            <button
              ref={closeButtonRef}
              type="button"
              className="workspace-selection-close"
              aria-label="Закрыть рабочую подборку"
              title="Закрыть рабочую подборку"
              onClick={requestClose}
            >
              ×
            </button>
          </div>
        </header>

        <div className="workspace-selection-body">
          {items.length === 0 ? (
            <div className="workspace-selection-empty">
              <div className="workspace-selection-empty__icon" aria-hidden="true">◎</div>
              <h3 className="workspace-selection-empty__title">Подборка пока пуста</h3>
              <p className="workspace-selection-empty__text">
                Добавьте ЖК, клиента или СИП из соответствующего раздела админки.
              </p>
            </div>
          ) : (
            <>
              {groups.complexes.length > 0 && (
                <SelectionGroup
                  title="ЖК"
                  count={groups.complexes.length}
                  openLabel="Открыть в Базе ЖК"
                  onOpen={() => openGroup("complexes")}
                  actions={(
                    <BulkComplexTableEditorAction
                      items={groups.complexes}
                      sourceSection="workspace-selection"
                      buttonClassName={actionClassName}
                    />
                  )}
                >
                  {groups.complexes.map((item) => (
                    <article key={getWorkspaceSelectionKey(item)} className="workspace-selection-card">
                      <div className="workspace-selection-card__top">
                        <div className="workspace-selection-card__content">
                          <h4 className="workspace-selection-card__title">
                            {item.snapshot?.complexName || "Без названия"}
                          </h4>
                          {typeof item.snapshot?.rootStatus === "boolean" && (
                            <div className="workspace-selection-card__badges">
                              <AdminStatusBadge active={item.snapshot.rootStatus} entity="complex" />
                            </div>
                          )}
                        </div>
                        <div className="workspace-selection-card__top-actions">
                          <ActionIconButton
                            icon="power"
                            label="Остановить ЖК"
                            tone="danger"
                            disabled={item.snapshot?.rootStatus === false || stoppingComplexIds.has(String(item.complexId))}
                            onClick={() => handleStopComplex(item)}
                          />
                          <RemoveButton
                            label={`Удалить ЖК ${item.snapshot?.complexName || "без названия"} из подборки`}
                            onClick={() => removeItem(item)}
                          />
                        </div>
                      </div>
                      <div className="workspace-selection-card__meta">
                        <MetaRow label="Город">{item.snapshot?.city || "—"}</MetaRow>
                      </div>
                    </article>
                  ))}
                </SelectionGroup>
              )}

              {groups.sipClients.length > 0 && (
                <SelectionGroup
                  title="Клиенты"
                  count={groups.sipClients.length}
                  openLabel="Открыть в Клиентах"
                  onOpen={() => openGroup("sipClients")}
                  actions={(
                    <>
                      <BulkSipTableEditorAction
                        items={groups.sipClients}
                        label="Редактировать таблицу клиентов"
                        sourceSection="workspace-selection"
                        buttonClassName={actionClassName}
                      />
                      <BulkSipStatusActions
                        items={groups.sipClients}
                        sourceSection="workspace-selection"
                        buttonClassName={actionClassName}
                        dangerButtonClassName={`${actionClassName} ${actionClassName}--danger`}
                      />
                    </>
                  )}
                >
                  {groups.sipClients.map((item) => (
                    <article key={getWorkspaceSelectionKey(item)} className="workspace-selection-card">
                      <div className="workspace-selection-card__top">
                        <div className="workspace-selection-card__content">
                          <h4 className="workspace-selection-card__title">
                            {item.clientName || "Без имени"}
                          </h4>
                          <div className="workspace-selection-card__badges">
                            {typeof item.snapshot?.clientStatus === "boolean" && (
                              <AdminStatusBadge active={item.snapshot.clientStatus} />
                            )}
                            <SessionChangedBadge item={item} />
                          </div>
                        </div>
                        <RemoveButton
                          label={`Удалить клиента ${item.clientName || "без имени"} из подборки`}
                          onClick={() => removeItem(item)}
                        />
                      </div>
                      <div className="workspace-selection-card__meta">
                        <MetaRow label="ЖК">{item.snapshot?.complexName || "—"}</MetaRow>
                        <MetaRow label="СИП">{item.snapshot?.sipNumber || "—"}</MetaRow>
                        <MetaRow label="Номер перевода">{item.snapshot?.transferNumber || "—"}</MetaRow>
                      </div>
                    </article>
                  ))}
                </SelectionGroup>
              )}

              {groups.transfers.length > 0 && (
                <SelectionGroup
                  title="СИПы"
                  count={groups.transfers.length}
                  openLabel="Открыть в СИПах"
                  onOpen={() => openGroup("transfers")}
                  actions={(
                    <BulkSipTableEditorAction
                      items={groups.transfers}
                      entityType="transfer"
                      label="Редактировать таблицу СИПов"
                      sourceSection="workspace-selection"
                      buttonClassName={actionClassName}
                    />
                  )}
                >
                  {groups.transfers.map((item) => (
                    <article key={getWorkspaceSelectionKey(item)} className="workspace-selection-card">
                      <div className="workspace-selection-card__top">
                        <div className="workspace-selection-card__content">
                          <h4 className="workspace-selection-card__title">
                            Номер перевода {item.transfer_number || "—"}
                          </h4>
                          {typeof item.clientStatus === "boolean" && (
                            <div className="workspace-selection-card__badges">
                              <AdminStatusBadge active={item.clientStatus} />
                            </div>
                          )}
                        </div>
                        <RemoveButton
                          label={`Удалить СИП ${item.transfer_number || "без номера"} из подборки`}
                          onClick={() => removeItem(item)}
                        />
                      </div>
                      <div className="workspace-selection-card__meta">
                        <MetaRow label="ЖК">{item.complexName || "—"}</MetaRow>
                        <MetaRow label="Клиент">{item.clientName || "—"}</MetaRow>
                      </div>
                    </article>
                  ))}
                </SelectionGroup>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
